import "../css/concierge-typeahead.css";

/* Accessible city / ZIP typeahead backed by /activity-concierge/places (the bundled US geo dataset).
   ARIA combobox + listbox, debounced, keyboard-navigable (↑/↓/Enter/Esc). Selecting a place fills the
   location input and auto-fills a paired ZIP input. Free-form stays allowed: if the visitor doesn't pick a
   suggestion, whatever they typed is used verbatim — so "not listed" needs no extra step. */
export function attachPlaceTypeahead(input, options) {
  options = options || {};
  if (!input || input.dataset.typeaheadReady === "true") return;

  var list = document.getElementById(input.getAttribute("aria-controls")) ||
    (input.parentElement && input.parentElement.querySelector(".fhq-typeahead__list"));
  if (!list) return;
  input.dataset.typeaheadReady = "true";

  var endpoint = options.endpoint || "/activity-concierge/places";
  var zipInput = options.zipInput || null;
  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 160;

  var items = [];
  var activeIndex = -1;
  var debounceTimer = null;
  var lastQuery = null;
  var seq = 0; // request sequence — drop stale responses
  var autofilledZip = null; // the ZIP we last auto-filled, so re-selecting a place updates it without clobbering a hand-typed ZIP

  function optionId(i) { return list.id + "-opt-" + i; }

  function close() {
    if (list.hidden && !items.length) return;
    list.hidden = true;
    list.innerHTML = "";
    items = [];
    activeIndex = -1;
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
  }

  function setActive(i) {
    activeIndex = i;
    var lis = list.children;
    for (var k = 0; k < lis.length; k++) {
      var on = k === i;
      lis[k].classList.toggle("is-active", on);
      lis[k].setAttribute("aria-selected", on ? "true" : "false");
    }
    if (i >= 0) {
      input.setAttribute("aria-activedescendant", optionId(i));
      if (lis[i].scrollIntoView) lis[i].scrollIntoView({ block: "nearest" });
    } else {
      input.removeAttribute("aria-activedescendant");
    }
  }

  function choose(i) {
    var place = items[i];
    if (!place) return;
    input.value = place.label;
    if (zipInput && place.zip) {
      var currentZip = zipInput.value.trim();
      // Fill when the ZIP is empty OR still holds a value we auto-filled (so changing the place updates it),
      // but never overwrite a ZIP the visitor typed themselves.
      if (currentZip === "" || currentZip === autofilledZip) {
        zipInput.value = place.zip;
        autofilledZip = place.zip;
      }
    }
    lastQuery = place.label;
    close();
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function render() {
    if (!items.length) { close(); return; }
    list.innerHTML = "";
    items.forEach(function (place, i) {
      var li = document.createElement("li");
      li.className = "fhq-typeahead__opt";
      li.id = optionId(i);
      li.setAttribute("role", "option");
      li.setAttribute("aria-selected", "false");
      li.textContent = place.label; // textContent → no HTML injection from the dataset
      li.addEventListener("mousedown", function (e) { e.preventDefault(); choose(i); }); // before blur
      li.addEventListener("mouseenter", function () { setActive(i); });
      list.appendChild(li);
    });
    list.hidden = false;
    input.setAttribute("aria-expanded", "true");
    setActive(-1);
  }

  function fetchSuggestions(q) {
    var mine = ++seq;
    fetch(endpoint + "?q=" + encodeURIComponent(q), {
      headers: { "X-Requested-With": "XMLHttpRequest" },
      credentials: "same-origin"
    })
      .then(function (r) { return r.ok ? r.json() : []; })
      .then(function (data) {
        if (mine !== seq) return;                  // a newer request superseded this one
        if (document.activeElement !== input) return;
        items = Array.isArray(data) ? data : [];
        render();
      })
      .catch(function () { /* best-effort; leave the field as plain free-text */ });
  }

  input.addEventListener("input", function () {
    var q = input.value.trim();
    if (q === lastQuery) return;
    lastQuery = q;
    if (debounceTimer) clearTimeout(debounceTimer);
    if (q.length < MIN_CHARS) { close(); return; }
    debounceTimer = setTimeout(function () { fetchSuggestions(q); }, DEBOUNCE_MS);
  });

  input.addEventListener("keydown", function (e) {
    if (list.hidden || !items.length) return; // closed → let the host form handle Enter (advance/submit)
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(activeIndex + 1 >= items.length ? 0 : activeIndex + 1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(activeIndex - 1 < 0 ? items.length - 1 : activeIndex - 1); }
    else if (e.key === "Enter" && activeIndex >= 0) { e.preventDefault(); e.stopPropagation(); choose(activeIndex); }
    else if (e.key === "Escape") { e.stopPropagation(); close(); }
  });

  input.addEventListener("blur", function () { setTimeout(close, 120); }); // let a mousedown selection land first

  // If the visitor edits the ZIP by hand, stop treating it as auto-filled so we won't overwrite it on re-selection.
  if (zipInput) {
    zipInput.addEventListener("input", function () {
      if (zipInput.value.trim() !== autofilledZip) autofilledZip = null;
    });
  }
}

/* "Use my location" — browser geolocation → /nearest → fills the location + ZIP (so the weather outlook works too).
   Keyless (browser geolocation + our bundled dataset). Hides itself when geolocation is unavailable OR the user has
   blocked location for the site (permission "denied"), so we never show a control that's guaranteed to fail.
   Degrades gracefully to a status message on a fresh denial / transient error. Reusable across the wizard + member form. */
export function attachUseMyLocation(button, options) {
  options = options || {};
  if (!button || button.dataset.geoReady === "true") return;
  if (!("geolocation" in navigator)) { button.style.display = "none"; return; }
  button.dataset.geoReady = "true";

  var endpoint = options.endpoint || "/activity-concierge/nearest";
  var locationInput = options.locationInput || null;
  var zipInput = options.zipInput || null;
  var onStatus = typeof options.onStatus === "function" ? options.onStatus : function () {};

  function hide() { button.style.display = "none"; }

  // Toggle on the actual permission state: hide the button when location is already blocked (e.g. denied earlier,
  // or a locked-down/incognito session) so it isn't a dead control. The Permissions API isn't universal (older
  // Safari), so we only HIDE on an explicit "denied"; on "prompt"/"granted"/unsupported we show and handle errors on click.
  try {
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: "geolocation" })
        .then(function (status) {
          if (status.state === "denied") hide();
          status.onchange = function () { if (status.state === "denied") hide(); };
        })
        .catch(function () { /* Permissions API unavailable — leave the button shown and rely on click-time handling. */ });
    }
  } catch (_) { /* query() can throw synchronously in some locked-down/incognito contexts — ignore and keep the button. */ }

  button.addEventListener("click", function () {
    var original = button.innerHTML;
    button.disabled = true;
    button.textContent = "Locating…";
    onStatus("");
    function done() { button.disabled = false; button.innerHTML = original; }

    navigator.geolocation.getCurrentPosition(
      function (pos) {
        fetch(endpoint + "?lat=" + encodeURIComponent(pos.coords.latitude) + "&lng=" + encodeURIComponent(pos.coords.longitude),
          { headers: { "X-Requested-With": "XMLHttpRequest" }, credentials: "same-origin" })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (place) {
            done();
            if (place && place.label && locationInput) {
              locationInput.value = place.label;
              if (zipInput && place.zip) zipInput.value = place.zip;
              locationInput.dispatchEvent(new Event("change", { bubbles: true }));
            } else {
              onStatus("We couldn't pin your area — type your city instead.");
            }
          })
          .catch(function () { done(); onStatus("We couldn't pin your area — type your city instead."); });
      },
      function (err) {
        done();
        if (err && err.code === 1) {
          // Permission denied — this session can't use it; drop the dead button and say why once.
          onStatus("Location access is off — just type your city instead.");
          hide();
        } else {
          // Transient (timeout / position unavailable) — keep the button so they can retry.
          onStatus("Couldn't get your location — try again, or just type your city.");
        }
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  });
}
